class PdpVariantsComponent extends HTMLElement {
  constructor() {
    super();
    this.section = this.closest('.pdp') || document;
    this.variantsList = this.querySelector('.pdp__variant-list');
    this.variants = this.querySelectorAll('.pdp__variant');
    this.productForm = this.section.querySelector('product-form-component');
    this.variantIdInput = this.productForm?.querySelector('[name="id"]');
    this.priceElement = this.section.querySelector('.pdp__price');
    this.discontinuedLabel = this.section.querySelector('[data-discontinued-label]');
    this.featureChipsContainer = this.section.querySelector('[data-pdp-feature-chips]');
    this.productData = null;
    this.mainSwiper = null;
    this.pendingGalleryVariantId = null;
    this.gallerySyncTimeout = null;
    this.gallerySyncAttempts = 0;
  }

  connectedCallback() {
    this.variants.forEach(variant => {
      variant.addEventListener('click', this.handleVariantClick.bind(this));
    });

    document.addEventListener('theme:variant:update', this.updateActiveVariant.bind(this));
    this.mainSwiper = this.section.querySelector('.gallerySwiperMain')?.swiper || null;
    this.loadProductData();
  }

  disconnectedCallback() {
    this.variants.forEach(variant => {
      variant.removeEventListener('click', this.handleVariantClick.bind(this));
    });
    document.removeEventListener('theme:variant:update', this.updateActiveVariant.bind(this));
    if (this.gallerySyncTimeout) {
      clearTimeout(this.gallerySyncTimeout);
      this.gallerySyncTimeout = null;
    }
  }

  async loadProductData() {
    try {
      const productUrl = this.productForm?.dataset.productUrl;
      if (!productUrl) return;

      const inlineProduct = this.getInlineProductData();
      const cachedProductData = this.getCachedProductData(productUrl);
      if (inlineProduct) {
        this.productData = inlineProduct;
      } else if (cachedProductData) {
        this.productData = cachedProductData;
      } else {
        const response = await fetch(`${productUrl}.js`, { cache: 'force-cache' });
        this.productData = await response.json();
        this.setCachedProductData(productUrl, this.productData);
      }

      // Initialize from URL variant, fallback to first available or first variant
      if (this.productData && this.productData.variants) {
        const urlVariantId = this.getVariantIdFromUrl();
        const urlVariant = this.productData.variants.find(v => v.id.toString() === urlVariantId);
        const initialVariant = urlVariant || this.productData.variants.find(v => v.available) || this.productData.variants[0];

        if (initialVariant) {
          this.updateActiveState(initialVariant.id.toString());
          if (this.variantIdInput) {
            this.variantIdInput.value = initialVariant.id;
          }
          this.updateProductDetails(initialVariant.id.toString());
        }
      }
    } catch (error) {
      console.error('Error loading product data:', error);
    }
  }

  handleVariantClick(event) {
    const variantElement = event.target.closest('.pdp__variant');
    if (!variantElement) return;

    const variantId = variantElement.dataset.variantId;

    this.updateActiveState(variantId);

    if (this.variantIdInput) {
      this.variantIdInput.value = variantId;
    }

    this.updateProductDetails(variantId);
  }

  updateActiveState(variantId) {
    this.variants.forEach(variant => {
      variant.classList.remove('pdp__variant--active');
      if (variant.dataset.variantId === variantId) {
        variant.classList.add('pdp__variant--active');
      }
    });
  }

  updateProductDetails(variantId) {
    if (!this.productData) return;

    const variant = this.productData.variants.find(v => v.id.toString() === variantId.toString());
    if (!variant) return;

    this.updatePrice(variant);
    this.updateStock(variant);
    this.updateAvailability(variant);
    this.updateDiscontinuedLabel(variant);
    this.updateQuantityInput(variant);
    this.updateGallery(variant);
    this.updateFeatureChips(variantId);
    this.updateURL(variantId);

    this.dispatchEvent(new CustomEvent('theme:variant:update', {
      bubbles: true,
      detail: {
        variantId: variantId,
        variant: variant
      }
    }));
  }

  updatePrice(variant) {
    if (!this.priceElement) return;

    const priceValue = this.priceElement.querySelector('.pdp__price-value');
    if (priceValue) {
      const formattedPrice = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: this.productData.currency || 'USD'
      }).format(variant.price / 100);

      priceValue.textContent = formattedPrice;
    }
  }

  updateStock(variant) {
    if (!this.priceElement) return;

    const stockElement = this.priceElement.querySelector('.pdp__stock');
    if (!stockElement) return;

    const showStock = stockElement.dataset.showStock === 'true';
    if (!showStock) {
      stockElement.style.display = 'none';
      return;
    }

    const inventoryManaged = variant.inventory_management === 'shopify';
    const inventoryQuantity = Number(variant.inventory_quantity || 0);
    const isSoldOut = this.isVariantSoldOut(variant);

    if (isSoldOut) {
      stockElement.textContent = stockElement.dataset.soldOutText || 'Sold out';
      stockElement.style.display = '';
      return;
    }

    if (!inventoryManaged) {
      stockElement.style.display = 'none';
      return;
    }

    if (inventoryQuantity <= 0) {
      stockElement.style.display = 'none';
      return;
    }

    const itemText = inventoryQuantity === 1 ? 'item' : 'items';
    const stockMessage = `<span>${inventoryQuantity}</span> ${itemText} in stock`;

    stockElement.innerHTML = stockMessage;
    stockElement.style.display = '';
  }

  updateAvailability(variant) {
    const addToCartButton = this.getAddToCartButton();
    if (!addToCartButton) return;

    const isAvailable = variant?.available === true && !this.isVariantSoldOut(variant);

    addToCartButton.disabled = !isAvailable;
    addToCartButton.textContent = isAvailable
      ? addToCartButton.dataset.addText || 'Add to cart'
      : addToCartButton.dataset.soldOutText || 'Sold out';
  }

  updateDiscontinuedLabel(variant) {
    if (!this.discontinuedLabel) return;

    this.discontinuedLabel.hidden = !this.isVariantSoldOut(variant);
  }

  isVariantSoldOut(variant) {
    if (!variant) return true;

    const inventoryManaged = variant.inventory_management === 'shopify';
    const inventoryQuantity = Number(variant.inventory_quantity || 0);

    return variant.available === false || (inventoryManaged && inventoryQuantity <= 0 && variant.inventory_policy === 'deny');
  }

  updateQuantityInput(variant) {
    const quantityInput = this.section.querySelector('.pdp__quantity input[name="quantity"]');
    if (!(quantityInput instanceof HTMLInputElement)) return;

    quantityInput.removeAttribute('max');
  }

  updateGallery(variant) {
    if (!variant.featured_media) return;

    const mainSwiper = this.mainSwiper || this.section.querySelector('.gallerySwiperMain')?.swiper;
    if (!mainSwiper) {
      this.mainSwiper = null;
      this.pendingGalleryVariantId = variant.id.toString();
      this.scheduleGallerySync();
      return;
    }
    this.mainSwiper = mainSwiper;
    this.pendingGalleryVariantId = null;
    this.gallerySyncAttempts = 0;

    const mediaId = variant.featured_media.id?.toString();
    const domSlides = Array.from(this.section.querySelectorAll('.gallerySwiperMain .swiper-slide'));
    const domIndex = domSlides.findIndex(slide => slide.dataset.mediaId === mediaId);

    if (domIndex !== -1) {
      mainSwiper.slideTo(domIndex);
      return;
    }

    const mediaIndex = this.productData.media.findIndex(m => m.id?.toString() === mediaId);
    if (mediaIndex !== -1) {
      mainSwiper.slideTo(mediaIndex);
    }
  }

  scheduleGallerySync() {
    if (this.gallerySyncTimeout || !this.pendingGalleryVariantId) return;

    this.gallerySyncTimeout = window.setTimeout(() => {
      this.gallerySyncTimeout = null;

      const pendingVariantId = this.pendingGalleryVariantId;
      if (!pendingVariantId || !this.productData?.variants) return;

      const pendingVariant = this.productData.variants.find(v => v.id.toString() === pendingVariantId);
      if (!pendingVariant) return;

      this.gallerySyncAttempts += 1;
      this.updateGallery(pendingVariant);

      if (this.pendingGalleryVariantId && this.gallerySyncAttempts < 25) {
        this.scheduleGallerySync();
      }
    }, 80);
  }

  updateURL(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url);
  }

  updateFeatureChips(variantId) {
    const container = this.featureChipsContainer || this.section.querySelector('[data-pdp-feature-chips]');
    if (!container) return;
    this.featureChipsContainer = container;

    const dataId = container.dataset.featureDataId;
    if (!dataId) return;

    const dataScript = document.getElementById(dataId);
    if (!(dataScript instanceof HTMLScriptElement)) return;

    let parsedData;
    try {
      parsedData = JSON.parse(dataScript.textContent || '{}');
    } catch (_error) {
      return;
    }

    const variants = parsedData?.variants || {};
    const variantData = variants?.[variantId?.toString?.() || ''] || {};
    const currentMaterial = this.normalizeChipValue(variantData.material);

    const chipValues = {
      backing: this.normalizeChipValue(variantData.backing),
      size: this.normalizeChipValue(variantData.size),
      material: currentMaterial,
      bulk_discount_label: this.normalizeChipValue(variantData.bulk_discount_label),
    };

    Object.entries(chipValues).forEach(([chipKey, chipValue]) => {
      const chipEl = container.querySelector(`[data-chip-key="${chipKey}"]`);
      if (!(chipEl instanceof HTMLElement)) return;

      if (chipValue) {
        chipEl.hidden = false;
        if (chipKey === 'bulk_discount_label') {
          const labelEl = chipEl.querySelector('[data-chip-label]');
          if (labelEl instanceof HTMLElement) labelEl.textContent = chipValue;

          const tooltipContentEl = chipEl.querySelector('[data-bulk-tooltip-content]');
          if (tooltipContentEl instanceof HTMLElement) {
            tooltipContentEl.innerHTML = this.getBulkPricingMarkup(currentMaterial, chipValues.bulk_discount_label, variantData);
          }

          const kickerEl = chipEl.querySelector('[data-bulk-tooltip-kicker]');
          if (kickerEl instanceof HTMLElement) {
            kickerEl.textContent = this.isCoinLabel(chipValues.bulk_discount_label) ? 'Bulk Coin Pricing' : 'Bulk Patch Pricing';
          }
        } else {
          chipEl.textContent = chipValue;
        }
      } else {
        chipEl.hidden = true;
        if (chipKey === 'bulk_discount_label') {
          const labelEl = chipEl.querySelector('[data-chip-label]');
          if (labelEl instanceof HTMLElement) labelEl.textContent = '';
          const tooltipContentEl = chipEl.querySelector('[data-bulk-tooltip-content]');
          if (tooltipContentEl instanceof HTMLElement) tooltipContentEl.innerHTML = '';
        } else {
          chipEl.textContent = '';
        }
      }
    });
  }

  getBulkPricingMarkup(material, label, variantData = {}) {
    const materialKey = `${material || ''}`.toLowerCase();
    const labelKey = `${label || ''}`.trim().toLowerCase();

    // Name tags (ADR-009): the Liquid block decides eligibility exactly as the
    // discount function does, including the shop switch, and passes the result.
    // The cart is the source of truth: while the switch moves between rules the
    // cart gives each line the better of the two, so this text is never more
    // than the cart charges.
    const nameTagTip = `${variantData?.name_tag_tip || ''}`;
    if (nameTagTip === 'none') return '';
    if (nameTagTip.startsWith('nt:')) {
      return this.getNameTagMarkup(nameTagTip.slice(3), variantData.name_tag_price);
    }

    // Coins (ADR-010): the label alone selects the coin table, as in the
    // discount function, counted per coin design.
    if (this.isCoinLabel(labelKey)) {
      return `
        <p class="pdp__bulk-tooltip-title">Coins</p>
        <p class="pdp__bulk-tooltip-minimum">Minimum order: 25</p>
        <p>25-49: $10.00 each</p>
        <p>50-99: $8.50 each</p>
        <p>100-199: $7.50 each</p>
        <p>200-299: $6.50 each</p>
        <p>300+: $5.00 each</p>
        <p>Counts per coin design.</p>
        <p>The cart shows your final price.</p>
      `;
    }

    if (materialKey.includes('leather') && labelKey === 'bulk leather visor covers') {
      return `
        <p class="pdp__bulk-tooltip-title">Leather Visor Covers</p>
        <p>Singles up to 10: $100.00 each</p>
        <p>10+: $75.00 each</p>
      `;
    }

    if (labelKey === 'bulk key rings') {
      return `
        <p class="pdp__bulk-tooltip-title">Key Rings</p>
        <p class="pdp__bulk-tooltip-minimum">Minimum order: 100</p>
        <p>100-199: $3.00 each</p>
        <p>200-299: $2.50 each</p>
        <p>300+: $2.00 each</p>
      `;
    }

    if (labelKey === 'bulk stickers') {
      return `
        <p class="pdp__bulk-tooltip-title">Stickers</p>
        <p class="pdp__bulk-tooltip-minimum">Minimum order: 25</p>
        <p>25-49: $3.25 each</p>
        <p>50-99: $2.75 each</p>
        <p>100-199: $2.00 each</p>
        <p>200-299: $1.25 each</p>
        <p>300+: $0.90 each</p>
      `;
    }

    if (materialKey.includes('embroidered')) {
      return `
        <p class="pdp__bulk-tooltip-title">Embroidered</p>
        <p class="pdp__bulk-tooltip-minimum">Minimum order: 25</p>
        <p>25-49: $6.00 each</p>
        <p>50-99: $5.00 each</p>
        <p>100-199: $4.00 each</p>
        <p>200-299: $3.00 each</p>
        <p>300+: $2.50 each</p>
      `;
    }

    if (materialKey.includes('pvc') || materialKey.includes('tpu') || materialKey.includes('ir')) {
      return `
        <p class="pdp__bulk-tooltip-title">PVC / TPU / IR</p>
        <p class="pdp__bulk-tooltip-minimum">Minimum order: 50</p>
        <p>50-99: $6.00 each</p>
        <p>100-199: $5.00 each</p>
        <p>200+: $4.00 each</p>
      `;
    }

    if (materialKey.includes('leather')) {
      return `
        <p class="pdp__bulk-tooltip-title">Leather</p>
        <p class="pdp__bulk-tooltip-minimum">Minimum order: 10</p>
        <p>10+ Antique: $55.00 each</p>
        <p>10+ Full Color Hand Painted: $95.00 each</p>
      `;
    }

    return '';
  }

  isCoinLabel(label) {
    return `${label || ''}`.trim().toLowerCase() === 'bulk coins';
  }

  getNameTagMarkup(pool, price) {
    let mixLine = `Any mix of ${pool} name tags in one order counts toward 10.`;
    if (pool === 'single') mixLine = 'Only more of this same name tag counts toward 10.';
    if (pool === 'same') mixLine = 'Any mix of name tags of the same material in one order counts toward 10.';
    return `
        <p class="pdp__bulk-tooltip-title">Name Tags</p>
        <p>1-9: ${price} each</p>
        <p>10+: $10.00 each</p>
        <p>${mixLine}</p>
        <p>The cart shows your final price.</p>
      `;
  }

  normalizeChipValue(value) {
    if (value === null || value === undefined) return '';

    if (Array.isArray(value)) {
      const normalized = value
        .map((item) => (item === null || item === undefined ? '' : `${item}`.trim()))
        .filter(Boolean);
      return normalized.join(', ');
    }

    if (typeof value === 'object') {
      if (value.value !== undefined && value.value !== null) {
        return `${value.value}`.trim();
      }
      return '';
    }

    return `${value}`.trim();
  }

  updateActiveVariant(event) {
    const variantId = event.detail?.variantId;
    if (variantId) {
      this.updateActiveState(variantId.toString());
    }
  }

  getVariantIdFromUrl() {
    const url = new URL(window.location.href);
    const variantId = url.searchParams.get('variant');
    return variantId ? variantId.toString() : '';
  }

  getAddToCartButton() {
    if (this.productForm) {
      return this.productForm.querySelector('[ref="addToCartButton"]') || this.productForm.querySelector('.pdp__add-to-cart');
    }
    return null;
  }

  getCachedProductData(productUrl) {
    try {
      const cacheKey = `pdpProductData:${productUrl}`;
      const cached = sessionStorage.getItem(cacheKey);
      if (!cached) return null;
      const payload = JSON.parse(cached);
      if (!payload?.data || !payload?.timestamp) return null;
      const maxAgeMs = 5 * 60 * 1000;
      if (Date.now() - payload.timestamp > maxAgeMs) return null;
      return payload.data;
    } catch (error) {
      return null;
    }
  }

  setCachedProductData(productUrl, data) {
    try {
      const cacheKey = `pdpProductData:${productUrl}`;
      const payload = {
        timestamp: Date.now(),
        data: data,
      };
      sessionStorage.setItem(cacheKey, JSON.stringify(payload));
    } catch (error) {
    }
  }

  getInlineProductData() {
    try {
      const script = this.section.querySelector('[data-product-json]');
      if (!script) return null;
      const parsed = JSON.parse(script.textContent || '{}');
      if (!parsed?.variants) return null;
      return parsed;
    } catch (_) {
      return null;
    }
  }
}

if (!customElements.get('pdp-variants-component')) {
  customElements.define('pdp-variants-component', PdpVariantsComponent);
}
